import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useStartRefinementMutation } from '../store/apis/rubric-refinement.api'
import { rubricRefinementSelectors } from '../store/selectors/rubricRefinement.selectors'
import { useParams } from 'react-router'
import useRubricContext from './useRubricContext'

/**
 * Custom hook to handle the rubric refinement initialization process.
 *
 * This hook:
 * 1. Starts the refinement process in the background when the component mounts
 * 2. Monitors the questionRubric's processing status
 * 3. Restarts the refinement process when initialization is complete
 * 4. Only shows loading indicators after the user has clicked "Let's get started"
 *
 * @returns {Object} An object containing the loading state
 */
export const useRefinementInitialization = () => {
    // Redux state
    const refinementProcess = useSelector(rubricRefinementSelectors.refinementProcess)
    const refinementItems = useSelector(rubricRefinementSelectors.refinementItems) || []
    const hasStarted = useSelector(rubricRefinementSelectors.hasStarted)

    // Route params
    const { token, layerId: questionRubricId } = useParams()

    // Get current questionRubric from context (enriched with categories)
    const { questionRubric, isFetching: isContextFetching } = useRubricContext()

    // API hooks
    const [startRefinement, { isLoading: isStartLoading }] = useStartRefinementMutation()

    // Refs to track state between renders
    const prevStatusRef = useRef(null)
    const hasInitializedRef = useRef(false)

    // Start the refinement process when the component mounts
    useEffect(() => {
        if (!hasInitializedRef.current && questionRubricId && questionRubricId !== 'all' && questionRubricId !== 'dashboard') {
            startRefinement({ questionRubricId, token })
            hasInitializedRef.current = true
        }
    }, [startRefinement, questionRubricId, token])

    // Monitor the questionRubric's processing status and restart refinement when initialization is complete
    useEffect(() => {
        const currentStatus = questionRubric?.processing?.status

        const isInitializationComplete =
            prevStatusRef.current === 'ip' && // Previous status was REFINEMENT_INITIALIZING
            currentStatus === 'f' && // Current status is READY
            refinementProcess?.status === 'Initializing' // But refinement process still shows initializing

        if (isInitializationComplete) {
            startRefinement({ questionRubricId, token })
        }

        // Update the previous status for the next check
        if (currentStatus) {
            prevStatusRef.current = currentStatus
        }
    }, [questionRubric, questionRubricId, startRefinement, token, refinementProcess])

    // Only consider the process to be loading if the user has started the refinement
    const isLoading =
        hasStarted &&
        (isStartLoading ||
            isContextFetching ||
            !refinementItems.length ||
            refinementProcess?.status === 'Initializing')

    return { isLoading }
}
